import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { Loader2, Search, PackageCheck, Truck, Boxes, CheckCircle2 } from 'lucide-react';
import BranchShell from '../components/BranchShell';
import { useBranchAuth, API } from '../context/BranchAuthContext';
import { useToast } from '../hooks/use-toast';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { formatDate } from '../lib/dateFormat';

/**
 * Feb 2026 — BranchReceiving (Branch FEFO WMS · Phase 2)
 *
 * A branch-scoped ASN Receiving + Put-Away workbench. Lists inbound
 * ASNs targeting the branch's assigned warehouse, exposes a Vehicle #
 * search filter, and lets branch users complete put-away tasks. All
 * endpoints under `/api/branch/*` server-side enforce the warehouse
 * scope so ops can never accidentally touch another branch's data.
 */
const BranchReceiving = () => {
  const { token, authHeaders } = useBranchAuth();
  const { toast } = useToast();
  const [asns, setAsns] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('expected');
  const [vehicleQuery, setVehicleQuery] = useState('');
  const [busyAsn, setBusyAsn] = useState('');
  const [busyTask, setBusyTask] = useState('');
  const [putawayForm, setPutawayForm] = useState({});

  const loadAsns = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      if (vehicleQuery.trim()) params.vehicle = vehicleQuery.trim();
      const { data } = await axios.get(`${API}/branch/asn`, { headers: authHeaders, params });
      setAsns(data?.asns || []);
    } catch (err) {
      toast({ title: 'Failed to load ASNs', description: err?.response?.data?.detail || 'Try again.' });
    }
  }, [authHeaders, statusFilter, vehicleQuery, toast]);

  const loadTasks = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/branch/warehouse/putaway-tasks`, { headers: authHeaders });
      setTasks(data?.tasks || []);
    } catch (err) {
      toast({ title: 'Failed to load put-away tasks', description: err?.response?.data?.detail || 'Try again.' });
    }
  }, [authHeaders, toast]);

  useEffect(() => {
    if (!token) return;
    (async () => {
      setLoading(true);
      await Promise.all([loadAsns(), loadTasks()]);
      setLoading(false);
    })();
  }, [token, loadAsns, loadTasks]);

  const receiveAsn = async (asnId) => {
    if (!window.confirm('Mark this ASN as received? Stock will land in the Stage zone and put-away tasks will be created.')) return;
    setBusyAsn(asnId);
    try {
      await axios.post(`${API}/branch/asn/${asnId}/receive`, {}, { headers: authHeaders });
      toast({ title: 'ASN received', description: 'Put-away tasks queued below.' });
      await Promise.all([loadAsns(), loadTasks()]);
    } catch (err) {
      toast({ title: 'Receive failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setBusyAsn(''); }
  };

  const completePutaway = async (taskId) => {
    const form = putawayForm[taskId] || {};
    if (!form.location_id) {
      toast({ title: 'Enter a destination', description: 'Location is required to complete a put-away.' });
      return;
    }
    setBusyTask(taskId);
    try {
      await axios.post(`${API}/branch/warehouse/putaway-tasks/${taskId}/complete`, {
        zone: form.zone || 'GEN',
        location_id: form.location_id,
        pallet_id: form.pallet_id || '',
      }, { headers: authHeaders });
      toast({ title: 'Put-away complete' });
      setPutawayForm((prev) => { const n = { ...prev }; delete n[taskId]; return n; });
      await loadTasks();
    } catch (err) {
      toast({ title: 'Put-away failed', description: err?.response?.data?.detail || 'Try again.' });
    } finally { setBusyTask(''); }
  };

  const filteredAsns = useMemo(() => asns, [asns]);

  if (!token) return <Navigate to="/branch/login" replace />;

  return (
    <BranchShell>
      <div className="max-w-6xl mx-auto px-4 py-6 space-y-6" data-testid="branch-receiving-page">
        <header className="flex flex-wrap items-baseline justify-between gap-2">
          <div>
            <h1 className="text-2xl font-extrabold text-neutral-900">ASN Receiving &amp; Put-Away</h1>
            <p className="text-sm text-neutral-500">Only ASNs targeting your warehouse appear here.</p>
          </div>
          {loading && <Loader2 className="w-4 h-4 animate-spin text-neutral-500" />}
        </header>

        {/* Filters */}
        <section className="rounded-2xl border border-neutral-200 bg-white p-4 flex flex-wrap gap-3 items-end">
          <label className="flex-1 min-w-[10rem] block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-600 block mb-1">Status</span>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-full rounded-md border border-neutral-300 bg-white px-3 py-2 text-sm"
              data-testid="branch-receiving-status"
            >
              <option value="">All</option>
              <option value="expected">Expected</option>
              <option value="received">Received</option>
              <option value="cancelled">Cancelled</option>
            </select>
          </label>
          <label className="flex-[2] min-w-[14rem] block">
            <span className="text-[10px] uppercase tracking-widest font-bold text-neutral-600 block mb-1">Vehicle #</span>
            <div className="relative">
              <Search className="w-4 h-4 text-neutral-400 absolute left-2.5 top-1/2 -translate-y-1/2 pointer-events-none" />
              <Input
                value={vehicleQuery}
                onChange={(e) => setVehicleQuery(e.target.value)}
                placeholder="e.g. OM-42-XXX"
                className="pl-8 text-sm"
                data-testid="branch-receiving-vehicle-search"
              />
            </div>
          </label>
          <Button
            onClick={loadAsns}
            className="bg-[#0F172A] hover:bg-[#1E293B] text-white rounded-full"
            data-testid="branch-receiving-refresh"
          >
            Search
          </Button>
        </section>

        {/* ASN list */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Truck className="w-4 h-4 text-[#0078D4]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-700">Inbound ASNs</h2>
            <span className="text-xs text-neutral-500">({filteredAsns.length})</span>
          </div>
          {filteredAsns.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 py-8 text-center text-sm text-neutral-500" data-testid="branch-receiving-empty">
              No ASNs match your filters.
            </div>
          ) : (
            <div className="space-y-2">
              {filteredAsns.map((a) => (
                <article
                  key={a.id}
                  data-testid={`branch-asn-${a.id}`}
                  className="rounded-2xl border border-neutral-200 bg-white p-4 flex flex-wrap items-center gap-3"
                >
                  <div className="flex-1 min-w-[14rem]">
                    <div className="text-sm font-extrabold text-neutral-900">{a.asn_number || a.id}</div>
                    <div className="text-xs text-neutral-500 flex flex-wrap gap-x-3 mt-0.5">
                      <span>Status: <b className={a.status === 'received' ? 'text-emerald-700' : 'text-amber-700'}>{a.status}</b></span>
                      {a.vehicle_number && <span>Vehicle: <b>{a.vehicle_number}</b></span>}
                      <span>{(a.line_items || []).length} line{(a.line_items || []).length === 1 ? '' : 's'}</span>
                      <span>{a.created_at ? formatDate(a.created_at) : ''}</span>
                    </div>
                  </div>
                  {a.status === 'expected' && (
                    <Button
                      onClick={() => receiveAsn(a.id)}
                      disabled={busyAsn === a.id}
                      className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full"
                      data-testid={`branch-asn-receive-${a.id}`}
                    >
                      {busyAsn === a.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><PackageCheck className="w-4 h-4 mr-1" /> Mark received</>)}
                    </Button>
                  )}
                </article>
              ))}
            </div>
          )}
        </section>

        {/* Put-away tasks */}
        <section>
          <div className="flex items-center gap-2 mb-2">
            <Boxes className="w-4 h-4 text-[#0078D4]" />
            <h2 className="text-sm font-bold uppercase tracking-wider text-neutral-700">Open put-away tasks</h2>
            <span className="text-xs text-neutral-500">({tasks.length})</span>
          </div>
          {tasks.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-neutral-300 bg-neutral-50 py-8 text-center text-sm text-neutral-500" data-testid="branch-putaway-empty">
              Nothing to put away right now.
            </div>
          ) : (
            <div className="space-y-2">
              {tasks.map((t) => {
                const form = putawayForm[t.id] || {};
                return (
                  <article
                    key={t.id}
                    data-testid={`branch-putaway-${t.id}`}
                    className="rounded-2xl border border-neutral-200 bg-white p-4"
                  >
                    <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1 mb-2">
                      <div className="text-sm font-extrabold text-neutral-900">{t.product_name || t.product_id}</div>
                      <div className="text-xs text-neutral-500">Qty: <b>{t.qty || t.picked_qty || 0}</b></div>
                      <div className="text-xs text-neutral-500">From pallet: <b>{t.pallet_id || '—'}</b></div>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <Input
                        placeholder="Zone (e.g. GEN)"
                        value={form.zone || ''}
                        onChange={(e) => setPutawayForm((p) => ({ ...p, [t.id]: { ...p[t.id], zone: e.target.value } }))}
                        className="text-sm"
                        data-testid={`branch-putaway-zone-${t.id}`}
                      />
                      <Input
                        placeholder="Location (e.g. A-01-03)"
                        value={form.location_id || ''}
                        onChange={(e) => setPutawayForm((p) => ({ ...p, [t.id]: { ...p[t.id], location_id: e.target.value } }))}
                        className="text-sm"
                        data-testid={`branch-putaway-loc-${t.id}`}
                      />
                      <Input
                        placeholder="Dest pallet (optional)"
                        value={form.pallet_id || ''}
                        onChange={(e) => setPutawayForm((p) => ({ ...p, [t.id]: { ...p[t.id], pallet_id: e.target.value } }))}
                        className="text-sm"
                        data-testid={`branch-putaway-pallet-${t.id}`}
                      />
                    </div>
                    <div className="mt-2 flex justify-end">
                      <Button
                        onClick={() => completePutaway(t.id)}
                        disabled={busyTask === t.id}
                        className="bg-[#0078D4] hover:bg-[#005A9E] text-white rounded-full"
                        data-testid={`branch-putaway-complete-${t.id}`}
                      >
                        {busyTask === t.id ? <Loader2 className="w-4 h-4 animate-spin" /> : (<><CheckCircle2 className="w-4 h-4 mr-1" /> Complete</>)}
                      </Button>
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </BranchShell>
  );
};

export default BranchReceiving;
